"use client";

import { AlertCircle, Shield } from "lucide-react";
import Link from "next/link";

import { SUPERUSER_EMAILS } from "../../constants/auth";
import { Alert, AlertDescription, AlertTitle } from "../ui/alert";
import { Button } from "../ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../ui/card";

interface SuperuserGuardProps {
  children: React.ReactNode;
  user: {
    uid: string;
    email: string | null;
    customClaims: Record<string, unknown>;
  } | null;
  fallback?: React.ReactNode;
}

/**
 * Check if a user is a superuser based on custom claims or email
 */
function isSuperuser(
  user: SuperuserGuardProps["user"],
): boolean {
  if (!user) {
    return false;
  }

  // Check if user is a super admin (primary method)
  if (user.customClaims?.isSuperAdmin === true) {
    return true;
  }

  // Fallback: Check if user's email is in the hardcoded admin list
  if (user.email && SUPERUSER_EMAILS.includes(user.email as any)) {
    return true;
  }

  return false;
}

/**
 * SuperuserGuard component that protects routes requiring superuser access
 * 
 * This component checks for:
 * 1. isSuperAdmin custom claim (primary method)
 * 2. Hardcoded admin email fallback (from shared constants)
 * 
 * @example
 * // In a server component page
 * export default async function MyPage() {
 *   const user = await getServerSideUser();
 *   return (
 *     <SuperuserGuard user={user}>
 *       <MyProtectedContent />
 *     </SuperuserGuard>
 *   );
 * }
 */
export default function SuperuserGuard({
  children,
  user,
  fallback,
}: SuperuserGuardProps) {
  if (!user) {
    return (
      <div className="container mx-auto py-8 max-w-md">
        <Card>
          <CardHeader className="text-center">
            <div className="mx-auto w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4">
              <Shield className="w-6 h-6 text-blue-600" />
            </div>
            <CardTitle>Authentication Required</CardTitle>
            <CardDescription>
              Please sign in to access this area
            </CardDescription>
          </CardHeader>
          <CardContent className="text-center">
            <Button asChild>
              <Link href="/login">Sign In</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!isSuperuser(user)) {
    if (fallback) {
      return <>{fallback}</>;
    }

    return (
      <div className="container mx-auto py-8 max-w-2xl">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Access Denied</AlertTitle>
          <AlertDescription className="mt-2">
            You don&apos;t have superuser privileges to access this area.
            <br />
            <br />
            Superuser access is required to use the management interface.
            <br />
            <br />
            If you believe this is an error, please contact a system administrator.
            <br />
            <br />
            <Button asChild variant="outline" className="mt-4">
              <Link href="/">Return to Home</Link>
            </Button>
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return <>{children}</>;
}

